<script setup lang="ts">

import {RiLockPasswordLine, RiArrowRightUpLine} from "@remixicon/vue";
import Button from "@/components/UI/button/Button.vue";
import {Dialog, DialogPanel, TransitionChild, TransitionRoot} from "@headlessui/vue";
import {Form} from "vee-validate";
import {ref, watch} from "vue";
import { useUserStore } from "@/stores/user";
import Input from "@/components/UI/input/Input.vue";
import {useAuthStore} from "@/stores/auth";
import type { Policy } from "@/types";

const open = ref<boolean>(false)


const userStore = useUserStore()
const auth = useAuthStore()
const error = ref<boolean>(false)

const accepted = (policy: Policy) => {

  if (userStore.userDataPolices.length === 0) {
    userStore.userDataPolices = <Policy[]>userStore.user?.dataPolicies
  }

  userStore.userDataPolices.map(p => {
    if (p.id === policy.id) {
      p.accepted = !p.accepted
    }
  })
}

const onSubmit = async () => {

  if (userStore.userDataPolices.length === 0) {
    error.value = true
    return
  }

  const check = userStore.userDataPolices.find(p => !p.accepted)

  if (check) {
    error.value = true
    return
  }

  await userStore.updateUserDataPolicy(<string>userStore.user?.id)
  await userStore.getUser()
}

watch(() => userStore.user?.dataPolicies, () => {

  if (userStore.user?.dataPolicies?.length === 0) {
    open.value = false
    return;
  }

  let checkPolicy = userStore.user?.dataPolicies?.find(p => !p.accepted)

  if (checkPolicy) {
    open.value = true
  }

}, {immediate: true, deep: true})

</script>

<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-10" @close="$emit('close-modal')">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div class="bg-white sm:p-4 sm:pb-4 flex flex-col">
                <Form @submit="onSubmit">
                  <div class="flex flex-col px-8">
                    <div class="flex flex-col items-center justify-center mb-6">
                      <RiLockPasswordLine />
                      <h2 class="font-lufga font-bold text-3xl text-center">{{ $t('terms.title') }}</h2>
                      <p class="text-center">{{ $t('terms.explanation') }}</p>
                    </div>
                    <div class="grid grid-cols-1 gap-4">

                      <div role="alert" class="relative flex w-full px-4 py-4 text-base text-gray-900 rounded-lg font-regular bg-red-900/10"
                           style="opacity: 1;" v-if="error">
                        <div class="mr-12 ">{{ $t('terms.errorMsg') }}</div>
                      </div>

                      <div v-for="policy in userStore.user?.dataPolicies" :key="policy.id"
                           class="inline-flex items-center  px-2 pb-2 pt-4 mb-2">
                        <input
                            :id="policy.id"
                            type="checkbox"
                            :value="policy.accepted"
                            :checked="policy.accepted"
                            @click="accepted(policy)"
                            class="accent-gray-900 w-5 h-5 text-gray-900 bg-gray-100 border-gray-300 rounded focus:ring-0"
                            :name="policy.policyName">
                        <label
                            :for="policy.id"
                            class="ml-2 text-sm font-medium underline text-blue-600 ">
                          <a :href="policy.fileUrl" target="_blank" class="flex" :title="policy.policyName"><span>{{ policy.policyName }}</span> <RiArrowRightUpLine /> </a>
                        </label>
                      </div>

                      <div class="text-center px-24 flex flex-row justify-center items-center gap-4">
                        <Button :disable-false="true" :loading="userStore.isLoading">{{ $t('buttons.Done') }}</Button>
                      </div>
                    </div>
                  </div>
                </Form>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
